import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { fDateTime, fToNow } from 'src/utils/formatTime';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface TranscriptMessage {
  id: string;
  participantRole: string;
  displayName?: string;
  contentType: string;
  content: string;
  absoluteTime?: string;
  status?: string | null;
}

interface Props {
  title: string;
  subtitle?: string;
  messages: TranscriptMessage[];
  onBack: () => void;
}

const GROUP_WINDOW_MS = 5 * 60 * 1000;

/* ------------------------------------------------------------------ */
/*  Message bubble                                                     */
/* ------------------------------------------------------------------ */

function MessageBubble({
  msg,
  isFirst,
  isLast,
}: {
  msg: TranscriptMessage;
  isFirst: boolean;
  isLast: boolean;
}): React.ReactElement {
  const isCustomer = msg.participantRole === 'CUSTOMER';
  const isSystem = msg.participantRole === 'SYSTEM';

  if (isSystem) {
    return (
      <Typography
        variant="caption"
        color="text.secondary"
        textAlign="center"
        sx={{ my: 0.5, fontStyle: 'italic' }}
      >
        {msg.content}
      </Typography>
    );
  }

  const senderLabel = isCustomer ? 'You' : msg.displayName || 'Agent';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isCustomer ? 'flex-end' : 'flex-start',
        mt: isFirst ? 1 : 0.25,
      }}
    >
      {isFirst && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ px: 0.5, mb: 0.25 }}
        >
          {senderLabel}
        </Typography>
      )}
      <Box
        sx={{
          maxWidth: '80%',
          p: 1,
          borderRadius: 1.5,
          bgcolor: isCustomer ? 'primary.light' : 'grey.200',
          color: isCustomer ? 'primary.contrastText' : 'text.primary',
          fontSize: '0.875rem',
        }}
      >
        {msg.status === 'joined' ? (
          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
            {senderLabel} joined
          </Typography>
        ) : msg.status === 'left' ? (
          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
            {senderLabel} left
          </Typography>
        ) : msg.status === 'ended' ? (
          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
            Chat ended
          </Typography>
        ) : (
          <Typography variant="body2">{msg.content}</Typography>
        )}
      </Box>
      {isLast && msg.absoluteTime && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ px: 0.5, mt: 0.25 }}
        >
          {fToNow(msg.absoluteTime)}
        </Typography>
      )}
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/*  Transcript view — header (back + title) + scrollable message list  */
/*  Designed to sit INSIDE an existing container (Paper, Drawer, etc.) */
/*  — no z-index/portal of its own, so it never fights for stacking    */
/*  order with whatever it's embedded in.                              */
/* ------------------------------------------------------------------ */

export default function ChatTranscriptView({
  title,
  subtitle,
  messages,
  onBack,
}: Props): React.ReactElement {
  const groupFlags = React.useMemo(() => {
    const flags: { isFirst: boolean; isLast: boolean }[] = new Array(
      messages.length
    );
    for (let i = 0; i < messages.length; i++) {
      const msg = messages[i];
      const prev = messages[i - 1];
      const next = messages[i + 1];

      const isFirst =
        !prev ||
        prev.participantRole !== msg.participantRole ||
        (!!msg.absoluteTime &&
          !!prev.absoluteTime &&
          new Date(msg.absoluteTime).getTime() -
            new Date(prev.absoluteTime).getTime() >
            GROUP_WINDOW_MS);

      const isLast =
        !next ||
        next.participantRole !== msg.participantRole ||
        (!!next.absoluteTime &&
          !!msg.absoluteTime &&
          new Date(next.absoluteTime).getTime() -
            new Date(msg.absoluteTime).getTime() >
            GROUP_WINDOW_MS);

      flags[i] = { isFirst, isLast };
    }
    return flags;
  }, [messages]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
        }}
      >
        <IconButton size="small" sx={{ color: 'inherit' }} onClick={onBack}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Box>
          <Typography variant="subtitle2" fontWeight={600}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Messages */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.25,
        }}
      >
        {messages.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            sx={{ mt: 2 }}
          >
            No transcript available
          </Typography>
        ) : (
          messages.map((msg, index) => {
            const { isFirst, isLast } = groupFlags[index];
            return (
              <MessageBubble
                key={msg.id || index}
                msg={msg}
                isFirst={isFirst}
                isLast={isLast}
              />
            );
          })
        )}
      </Box>
    </Box>
  );
}

// Re-exported for callers that want a formatted session date label without
// duplicating the import.
export { fDateTime };
